import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDateTime } from '../../../config/appConfig';
import { Button } from '../../../components/ui';
import api from '../../../services/api';
import type { SystemHealthSnapshot } from '../types';
import HealthBadge from './HealthBadge';

interface EmailAccountHealthCardProps {
  account: SystemHealthSnapshot['emailSync']['accounts'][number];
  canManageSync?: boolean;
  onSyncPaused?: () => void | Promise<void>;
  onSyncResumed?: () => void | Promise<void>;
}

const EmailAccountHealthCard: React.FC<EmailAccountHealthCardProps> = ({
  account,
  canManageSync = false,
  onSyncPaused,
  onSyncResumed,
}) => {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(false);
  const isSyncing = account.syncStatus === 'syncing';

  const handlePause = async () => {
    if (!canManageSync || loading) return;
    setLoading(true);
    try {
      await api.post(`/system-health/email-sync/${account.id}/pause`);
      toast.success('Email sync paused');
      await onSyncPaused?.();
    } catch {
      toast.error('Failed to pause email sync');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!canManageSync || loading) return;
    setLoading(true);
    try {
      await api.post(`/system-health/email-sync/${account.id}/resume`);
      toast.success('Email sync resumed');
      await onSyncResumed?.();
    } catch {
      toast.error('Failed to resume email sync');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border p-4 flex flex-col gap-3 h-full',
        'border-sky-200/60 dark:border-sky-800/40',
        'bg-sky-50/30 dark:bg-sky-950/10',
        colors.surface,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={cn('font-semibold truncate', colors.text)}>{account.displayName || account.emailAddress}</p>
          {account.displayName && (
            <p className={cn('text-xs truncate mt-0.5', colors.textMuted)}>{account.emailAddress}</p>
          )}
        </div>
        <HealthBadge level={account.health} />
      </div>

      <p className={cn('text-sm', colors.textMuted)}>{account.healthMessage}</p>
      {isSyncing && (
        <p className={cn('text-xs font-medium text-sky-700 dark:text-sky-300')}>Sync in progress…</p>
      )}
      {account.syncError && <p className={cn('text-xs rounded-lg px-2 py-1.5', colors.error)}>{account.syncError}</p>}

      <dl className={cn('grid grid-cols-2 gap-x-3 gap-y-2 text-sm mt-auto pt-2 border-t', colors.border)}>
        <div>
          <dt className={colors.textMuted}>Last sync</dt>
          <dd className={cn('font-medium mt-0.5', colors.text)}>
            {account.lastSyncAt ? formatDateTime(account.lastSyncAt) : 'Never'}
          </dd>
        </div>
        <div>
          <dt className={colors.textMuted}>Interval</dt>
          <dd className={cn('font-medium mt-0.5', colors.text)}>
            {account.syncEnabled ? `${account.syncInterval} min` : 'Paused'}
          </dd>
        </div>
        <div>
          <dt className={colors.textMuted}>Next sync</dt>
          <dd className={cn('font-medium mt-0.5', colors.text)}>
            {!account.syncEnabled
              ? '—'
              : account.nextSyncInMinutes === 0
                ? 'Due now'
                : account.nextSyncInMinutes != null
                  ? `~${account.nextSyncInMinutes} min`
                  : '—'}
          </dd>
        </div>
        <div>
          <dt className={colors.textMuted}>Mode</dt>
          <dd className={cn('font-medium mt-0.5 truncate', colors.text)}>{account.imapMirrorMode || '—'}</dd>
        </div>
        <div>
          <dt className={colors.textMuted}>Cached emails</dt>
          <dd className={cn('font-medium mt-0.5 tabular-nums', colors.text)}>{account.emailCount.toLocaleString()}</dd>
        </div>
      </dl>

      {canManageSync && (
        <div className="flex flex-wrap gap-2 pt-1">
          {account.syncEnabled ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={loading}
              disabled={loading}
              onClick={() => void handlePause()}
            >
              {isSyncing ? 'Stop & pause sync' : 'Pause sync'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="default"
              size="sm"
              loading={loading}
              disabled={loading}
              onClick={() => void handleResume()}
            >
              Resume sync
            </Button>
          )}
        </div>
      )}

      <Link
        to={`/email-accounts/${account.id}`}
        className={cn('text-xs font-medium hover:opacity-80 transition-opacity', colors.primaryFg)}
      >
        Open account settings →
      </Link>
    </div>
  );
};

export default EmailAccountHealthCard;
